import { applicationFeedbackMessage } from "./sourceChangeApplicationFeedback.js";

const NON_OPERATIONAL_TRUCK_FIELDS = new Set([
  "truck_name", "compartment_name",
  "driver_name", "evening_driver_name",
  // The audit also reports persistence metadata on an otherwise unchanged save.
  "updated_at", "source_kind", "source_label",
]);

const PERIODS = [["day", "Jour"], ["evening", "Soir"]];
const TRUCK_DISPATCH_OPTIMIZATION_MESSAGE = "Optimisation du camion pour le Dispatch…";

// Saving the source succeeds independently of preparing either dispatch period.
export function saveTruckWithDispatchPreparation(options) {
  const existing = options.inFlight?.get(options.truckId);
  if (existing) return existing;
  const pending = saveAndPrepareTruck(options).finally(() => options.inFlight?.delete(options.truckId));
  options.inFlight?.set(options.truckId, pending);
  return pending;
}

// sectionLabel : section de Mise à jour de l'onglet sauvegardé (Camions ou Remorques).
async function saveAndPrepareTruck({
  truckId, payload, dispatchDate, saveTruck, prepareTruck,
  reportProgress = () => {}, setCompletionToast, updateLoadingToast = () => {},
  sectionLabel = "Camions", savedTitle = "Camion enregistré",
}) {
  const result = await saveTruck(truckId, payload);
  // Création : le code de la ligne vient de la réponse du serveur.
  const savedTruckId = truckId ?? result?.truck?.truck_id;
  reportProgress("save", "completed");
  const changedFields = result?.audit?.changed_fields;
  if (Array.isArray(changedFields) && changedFields.every((field) => NON_OPERATIONAL_TRUCK_FIELDS.has(field))) {
    reportProgress("day", "skipped");
    reportProgress("evening", "skipped");
    return result;
  }
  const savedResult = {
    ...result,
    _data_manager_success_toast_handled: true,
    _prepared_dispatch_date: dispatchDate,
  };
  if (!dispatchDate) {
    reportProgress("day", "warning");
    reportProgress("evening", "warning");
    setCompletionToast({
      title: `${savedTitle} — journée à sélectionner`,
      message: `Sélectionnez la journée de Dispatch à vérifier dans Mise à jour > ${sectionLabel}.`,
      type: "warning",
      duration: 9000,
    });
    return savedResult;
  }
  updateLoadingToast({
    title: `${savedTitle} — préparation du Dispatch`,
    message: TRUCK_DISPATCH_OPTIMIZATION_MESSAGE,
  });
  const summaries = [];
  let hasWarning = false;
  for (const [dispatchPeriod, label] of PERIODS) {
    reportProgress(dispatchPeriod);
    try {
      const preparation = await prepareTruck(savedTruckId, {
        dispatchDate,
        dispatchPeriod,
        expectedUpdatedAt: result?.truck_updated_at ?? result?.truck?.updated_at,
      });
      const evidence = { result_code: preparation?.reason_code || preparation?.outcome || "invalid_response" };
      // Tracteur partagé à cette date : un résultat par ligne préparée.
      if (Array.isArray(preparation?.results) && preparation.results.length > 1) {
        const lineBlocked = preparation.results.some((line) => line.outcome === "blocked");
        hasWarning ||= lineBlocked;
        reportProgress(dispatchPeriod, lineBlocked ? "warning" : preparation.outcome === "applied" ? "completed" : "skipped", evidence);
        summaries.push(`${label} : ${preparation.results.map((line) => `${line.truck_label} ${line.outcome === "applied"
          ? "actualisé" : line.outcome === "unchanged" ? "inchangé"
            : `bloqué (${applicationFeedbackMessage(line.application_feedback) || line.message})`}`).join(", ")}.`);
        continue;
      }
      if (preparation?.outcome === "applied") {
        reportProgress(dispatchPeriod, "completed", evidence);
        summaries.push(`${label} : camion actualisé.`);
      } else if (preparation?.outcome === "unchanged") {
        reportProgress(dispatchPeriod, "skipped", evidence);
        summaries.push(`${label} : ${preparation.message || "aucun changement à appliquer."}`);
      } else {
        hasWarning = true;
        reportProgress(dispatchPeriod, "warning", evidence);
        summaries.push(`${label} : ${applicationFeedbackMessage(preparation?.application_feedback) || preparation?.message || "camion à vérifier dans Mise à jour."}`);
      }
    } catch (error) {
      // A lost response may follow a commit. Never repeat the POST automatically;
      // the normal Data Manager mutation effects reload both dispatch periods.
      hasWarning = true;
      reportProgress(dispatchPeriod, "warning", { result_code: Number.isInteger(error?.status) ? `http_${error.status}` : "request_failed" });
      summaries.push(`${label} : ${error?.message || "préparation interrompue."}`);
    }
  }
  setCompletionToast({
    title: `${savedTitle} — ${hasWarning ? "Dispatch à vérifier" : "Dispatch vérifié"}`,
    message: `${summaries.join(" ")}${hasWarning ? ` Consultez Mise à jour > ${sectionLabel}.` : ""}`,
    type: hasWarning ? "warning" : "success",
    duration: hasWarning ? 12000 : 8000,
  });
  return savedResult;
}
